import type { ReactNode } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';

import { Icon } from '#/components/ui/icons';

function DefaultFallback({
  error,
  resetErrorBoundary,
  onReset,
}: FallbackProps & { onReset?: () => void }) {
  const message = error instanceof Error ? error.message : undefined;

  return (
    <div className="text-error flex gap-8" role="alert">
      <Icon id="exclamation" className="mt-2 text-current" />
      <div className="flow flow-6">
        <p>Something went wrong. Please try again.</p>
        <div className="flex gap-6">
          {message && <p className="text-static-12 text-slate-60">{message}</p>}
          {onReset && (
            <button
              type="button"
              onClick={resetErrorBoundary}
              className="text-link-fg hover:text-link-fg-hover text-static-12 underline decoration-dotted"
            >
              Try again?
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  resetKeys?: unknown[];
  onReset?: () => void;
  onError?: (error: Error, info: { componentStack?: string | null }) => void;
}

export function ErrorBoundary({
  children,
  fallback,
  resetKeys,
  onReset,
  onError,
}: ErrorBoundaryProps) {
  return (
    <ReactErrorBoundary
      fallbackRender={
        fallback
          ? () => <>{fallback}</>
          : ({ error, resetErrorBoundary }) => (
              <DefaultFallback
                error={error}
                resetErrorBoundary={resetErrorBoundary}
                onReset={onReset}
              />
            )
      }
      resetKeys={resetKeys}
      onReset={onReset}
      onError={(error, info) => {
        console.error(error);
        if (error instanceof Error) {
          onError?.(error, info);
        }
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}

export { useErrorBoundary } from 'react-error-boundary';
